import { useEffect, useRef } from 'react';
import { useViewsStore } from '@/stores';

interface KeyboardShortcutCallbacks {
  onCopyLink: () => void;
  onOpenTradeEntry: () => void;
  // Optional: when not supplied, the E / Cmd+E shortcuts pass through (no preventDefault).
  onEditTrade?: () => void;
  onExportCsv?: () => void;
  /** Shift+ArrowUp/Down: extend the range to the row focus just moved to. */
  onExtendSelectionTo?: (id: number) => void;
  getVisibleRowIds: () => number[];
  hasSelection: boolean;
  selectedCount: number;
}

export function useKeyboardShortcuts(callbacks: KeyboardShortcutCallbacks): void {
  // Use a ref to avoid stale closures — always read the latest callbacks
  const callbacksRef = useRef<KeyboardShortcutCallbacks>(callbacks);
  useEffect(() => {
    callbacksRef.current = callbacks;
  });

  useEffect(() => {
    const isInInput = (): boolean => {
      const el = document.activeElement;
      return (
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement
      );
    };

    const handleKeyDown = (e: KeyboardEvent): void => {
      const cbs = callbacksRef.current;

      // 1. Alt+Left / Alt+Right — Tab navigation (KEY-01)
      // No input guard per UI-SPEC: fires even when in inputs
      if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        const { openTabs, activeViewId } = useViewsStore.getState();
        const currentIndex = openTabs.indexOf(activeViewId);
        if (currentIndex === -1) return;
        const newIndex =
          e.key === 'ArrowLeft'
            ? Math.max(0, currentIndex - 1)
            : Math.min(openTabs.length - 1, currentIndex + 1);
        if (newIndex !== currentIndex) {
          useViewsStore.getState().activate(openTabs[newIndex]);
        }
        return;
      }

      // 2. Alt+1 through Alt+9 — Jump to tab N (KEY-01)
      // No input guard per UI-SPEC
      if (e.altKey && e.key >= '1' && e.key <= '9') {
        e.preventDefault();
        const { openTabs } = useViewsStore.getState();
        const tabIndex = parseInt(e.key, 10) - 1;
        if (tabIndex < openTabs.length) {
          useViewsStore.getState().activate(openTabs[tabIndex]);
        }
        return;
      }

      // 3. Cmd/Ctrl+L — Copy link (KEY-03)
      // Russian layout: Д/д (physical L key on Russian keyboard)
      if (
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        (e.key === 'l' || e.key === 'L' || e.key === 'д' || e.key === 'Д')
      ) {
        if (isInInput()) return;
        e.preventDefault();
        cbs.onCopyLink();
        return;
      }

      // 5. Cmd/Ctrl+K — Focus search (KEY-04)
      // Russian layout: Р/р or Л/л (physical K key on Russian keyboard produces Л/л)
      if (
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        (e.key === 'k' ||
          e.key === 'K' ||
          e.key === 'л' ||
          e.key === 'Л' ||
          e.key === 'р' ||
          e.key === 'Р')
      ) {
        if (isInInput()) return;
        // Only claim the keystroke when there is a search field to focus —
        // otherwise let the browser's native Ctrl+K through instead of
        // swallowing it.
        const searchInput = document.querySelector<HTMLInputElement>('input[placeholder*="Search"]');
        if (searchInput) {
          e.preventDefault();
          searchInput.focus();
        }
        return;
      }

      // 6. ArrowUp / ArrowDown — Navigate rows (KEY-05)
      if (
        (e.key === 'ArrowUp' || e.key === 'ArrowDown') &&
        !e.altKey &&
        !e.metaKey &&
        !e.ctrlKey
      ) {
        if (isInInput()) return;
        const isInTable =
          !!document.activeElement?.closest('[data-trades-table]') ||
          document.activeElement === document.body;
        if (!isInTable) return;
        e.preventDefault();

        const visibleIds = cbs.getVisibleRowIds();
        if (visibleIds.length === 0) return;

        const focusedEl = document.activeElement?.closest<HTMLElement>('[data-row-id]');
        let currentId: number | null = null;
        if (focusedEl) {
          const rawId = focusedEl.getAttribute('data-row-id');
          currentId = rawId ? parseInt(rawId, 10) : null;
        }

        let nextId: number;
        if (currentId === null) {
          // No row focused — go to first or last
          nextId = e.key === 'ArrowDown' ? visibleIds[0] : visibleIds[visibleIds.length - 1];
        } else {
          const currentIndex = visibleIds.indexOf(currentId);
          if (currentIndex === -1) {
            nextId = visibleIds[0];
          } else if (e.key === 'ArrowDown') {
            nextId = visibleIds[Math.min(visibleIds.length - 1, currentIndex + 1)];
          } else {
            nextId = visibleIds[Math.max(0, currentIndex - 1)];
          }
        }

        const nextRowEl = document.querySelector<HTMLElement>(`[data-row-id="${nextId}"]`);
        if (nextRowEl) nextRowEl.focus();
        // Shift held: extend the range to the row the ring moved onto, from the
        // anchor the last click or toggle placed (LR-699). Plain arrows only
        // move focus. Extending is by id, not gated on `nextRowEl`: the row is
        // in the book whether or not the virtualiser has it mounted, and the
        // focus call above has the same best-effort shape.
        if (e.shiftKey) cbs.onExtendSelectionTo?.(nextId);
        return;
      }

      // 7. Space belongs to the row, not here: it needs the modifiers to pick a
      // selection mode (LR-699). This listener captures, so while it also claimed
      // Space the row's own handler (LR-694) toggled a second time and cancelled it.

      // 8. Cmd/Ctrl+Enter — Open Trade Entry (KEY-06)
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        if (isInInput()) return;
        e.preventDefault();
        cbs.onOpenTradeEntry();
        return;
      }

      // NOTE: there is intentionally no Cmd/Ctrl+A "select all" — selecting the
      // whole filtered view is a button in the bulk action bar, not a shortcut.

      // 9. E (no modifier) — Edit selected trade (KEY-06)
      if (
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.shiftKey &&
        (e.key === 'e' || e.key === 'E' || e.key === 'у' || e.key === 'У')
      ) {
        if (isInInput()) return;
        if (!cbs.onEditTrade) return;
        if (cbs.selectedCount !== 1) return;
        cbs.onEditTrade();
        return;
      }

      // 10. Cmd/Ctrl+E — Export CSV (KEY-06)
      // Russian layout: У/у
      if (
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        (e.key === 'e' || e.key === 'E' || e.key === 'у' || e.key === 'У')
      ) {
        if (isInInput()) return;
        if (!cbs.onExportCsv) return;
        if (!cbs.hasSelection) return;
        e.preventDefault();
        cbs.onExportCsv();
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []); // Register once — reads latest callbacks via ref
}
